import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { Ballast, InsertBallastBody, UpdateBallastBody } from "./types/ballasts";

export const useBallastsApi = () => {
  const api = useApi();

  const getAdminBallast = useCallback(
    (ballastId: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/ballasts/${ballastId}`;
      return useSWRMutation<Ballast, CustomError, string>(url, (url) => api.get<Ballast>({ url, signal }));
    },
    [api],
  );

  const useAdminBallasts = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/ballasts`, query);
      return useSWRImmutable<Paginated<Ballast>, CustomError, string>(url, (url) =>
        api.get<Paginated<Ballast>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminBallasts = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/ballasts`, query);
      return api.get<Paginated<Ballast>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminBallast = useCallback(
    async (body: InsertBallastBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/ballasts`;
      const id = await api.post<{ id: string }, InsertBallastBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminBallastVisibility = useCallback(
    async (ballastId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/ballasts/${ballastId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache([`/admin/settings/border/ballasts`]));
    },
    [api],
  );

  const updateAdminBallast = useCallback(
    async (ballastId: string, body: UpdateBallastBody): Promise<void> => {
      const url = `/admin/settings/border/ballasts/${ballastId}`;
      (await api.put<UpdateBallastBody>(url, body), mutateCache([`/admin/settings/border/ballasts`, url]));
    },
    [api],
  );

  const updateAdminBallastOrder = useCallback(
    async (ballastId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/ballasts/${ballastId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order), mutateCache([`/admin/settings/border/ballasts`]));
    },
    [api],
  );

  const deleteAdminBallast = useCallback(
    async (ballast: Ballast): Promise<void> => {
      const url = `/admin/settings/border/ballasts/${ballast.item.id}`;
      await api.del(url);
      mutateCache([`/admin/settings/border/ballasts`, url]);
    },
    [api],
  );

  return {
    getAdminBallast,
    insertAdminBallast,
    updateAdminBallast,
    updateAdminBallastVisibility,
    updateAdminBallastOrder,
    deleteAdminBallast,
    useAdminBallasts,
    getAdminBallasts,
  };
};
